import { useEffect, useState } from 'react';

/**
 * Light/dark toggle. The source of truth is `data-theme` on <html>, set before first paint by the
 * inline no-FOUC script in index.html (from localStorage, else unset → follows the OS via the
 * prefers-color-scheme media query in tokens.css). Clicking PINS an explicit preference that then
 * wins over the OS setting; until then the toggle mirrors the system and reacts to OS changes.
 */

const STORAGE_KEY = 'huaca-theme';

type Theme = 'light' | 'dark';

function systemTheme(): Theme {
  // matchMedia is absent in jsdom and some embedded webviews — degrade to light rather than throw.
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function currentTheme(): Theme {
  const attr = document.documentElement.getAttribute('data-theme');
  if (attr === 'light' || attr === 'dark') return attr;
  return systemTheme();
}

export interface ThemeToggleProps {
  /**
   * `button` — the standalone bordered square this has always been.
   * `rail` — a full-width row in the side rail's footer, with a visible label.
   *
   * A VARIANT rather than a second component, because everything above this line is the part worth
   * not duplicating: the pinned-vs-OS distinction, the live `matchMedia` subscription, the storage
   * key that four other files agree with, and the `matchMedia`-is-absent guard that once took the
   * whole shell down. A rail-shaped copy would fork all of it to change two elements of markup.
   */
  variant?: 'button' | 'rail';
}

export function ThemeToggle({ variant = 'button' }: ThemeToggleProps = {}) {
  const [theme, setTheme] = useState<Theme>(() =>
    typeof document === 'undefined' ? 'light' : currentTheme(),
  );
  const [pinned, setPinned] = useState<boolean>(() =>
    typeof document === 'undefined' ? false : !!document.documentElement.getAttribute('data-theme'),
  );

  // While unpinned, follow the OS so the icon never lies about what the user is looking at.
  useEffect(() => {
    if (pinned || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setTheme(systemTheme());
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [pinned]);

  function toggle() {
    const next: Theme = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    setPinned(true);
    document.documentElement.setAttribute('data-theme', next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* private mode / storage disabled — the in-memory toggle still works for this session */
    }
  }

  const goingDark = theme === 'light';
  const label = goingDark ? 'מעבר למצב כהה' : 'מעבר למצב בהיר';

  if (variant === 'rail') {
    return (
      <button
        type="button"
        /*
          The rail's own item class, so this row inherits the group's alignment, hover, current-item
          treatment and collapsed-label handling instead of restating them. It is the reason there
          is no new CSS for this: the only thing that made the theme control look bolted on was that
          it was a 38px bordered square in a column of borderless full-width rows.
        */
        className="huaca-rail__item"
        onClick={toggle}
        data-testid="theme-toggle"
        /*
          A native tooltip in BOTH states, where a nav item gets one only while collapsed.
          `SideRail` hands this component through an opaque `children` slot and cannot tell it
          which state the rail is in — and of the two ways to be wrong, a redundant tooltip beside a
          visible label is better than a collapsed icon with no tooltip at all, which is what this
          control has never had to do without.
        */
        title={label}
      >
        {/* `huaca-icon` sizes the glyph to the 20px the rest of the column uses; without it these
            two SVGs keep their own 16px and sit visibly smaller than every icon beside them. */}
        <span className="huaca-rail__icon">
          {goingDark ? <MoonIcon className="huaca-icon" /> : <SunIcon className="huaca-icon" />}
        </span>
        {/* The visible text IS the accessible name here, so no aria-label: one wording, and the
            collapsed rail hides it from eyes only, exactly as it does for every nav item. */}
        <span className="huaca-rail__label">{label}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      className="huaca-theme-toggle"
      onClick={toggle}
      data-testid="theme-toggle"
      aria-label={label}
      title={label}
    >
      {goingDark ? <MoonIcon /> : <SunIcon />}
    </button>
  );
}

function SunIcon({ className }: { className?: string } = {}) {
  return (
    <svg
      {...(className !== undefined && { className })}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M6.3 17.7l-1.4 1.4M19.1 4.9l-1.4 1.4" />
    </svg>
  );
}

function MoonIcon({ className }: { className?: string } = {}) {
  return (
    <svg
      {...(className !== undefined && { className })}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
    </svg>
  );
}
